import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { getPlaygroundModeStatus } from '../../../http/Company/playgroundModeApi';
import queryKeys from './queryKeys';

/**
 * Whether QA Playground Mode is available (the QaPlaygroundEnabled env gate is on) and,
 * if so, whether it's currently active for the caller's own session (Feature #8075, PBI #8080).
 *
 * A 404 from GET /api/Company/playground-mode means the env gate is off (prod) — this is
 * `isUnavailable`, not an error: AC1 requires the toggle to not render at all in that case.
 *
 * Any other failure is deliberately NOT folded into the same "hidden" bucket (PR #2577
 * review, Koketso Ramokhubathi): the server holds Playground Mode state per user with an
 * 8-hour sliding expiration, independent of the browser, so a transient fetch error tells us
 * nothing about whether suppression is still active. `isActive` is read straight off
 * `query.data`, which React Query keeps from the last successful fetch through a failed
 * background refetch — so the banner (Feature #8075's only user-facing indicator) keeps
 * showing the last known-true state instead of silently claiming "normal mode". `isUnknown`
 * is only true when we have never had a successful fetch at all and the failure wasn't a 404
 * — the one case where there is truly no state to fall back on.
 *
 * This is UX only — it never decides suppression itself, it only reflects and requests
 * state via the US-2/US-3/US-4 backend, which is the sole enforcement point.
 *
 * Bug #8517: a tab in a separate, genuinely visible window — never losing/regaining real OS
 * focus (the common QA pattern: two windows side by side) — hit none of React Query's three
 * auto-refetch triggers (mount, focus, reconnect), so it kept showing a stale "ON" indefinitely
 * after another tab turned Playground Mode off there, with no reload to force the mount trigger.
 * `refetchOnWindowFocus: true` is stated explicitly below (it was already the inherited default,
 * same convention as COI_LIST_QUERY_OPTIONS's documented cross-tab intent) and `refetchInterval`
 * closes the gap neither that nor staleTime:0 can: a visible-but-unfocused window now
 * self-corrects on its own within 30s, same precedent as VERIFICATION_PENDING_POLL_INTERVAL_MS —
 * shorter than that 60s because this is a cheap in-process store read (not an external provider
 * call to amortise), and because the wrong-direction miss here means a tester believes real
 * sends are suppressed when they are not.
 *
 * Narrower than it first sounds (auto-review finding, fp:refetch-interval-paused-in-background-tab,
 * PR #2612): `refetchIntervalInBackground` defaults to false, and a tab merely stacked BEHIND
 * another tab in the same window reports `visibilityState: 'hidden'`, so the 30s timer does not
 * run there at all — only in a second, genuinely visible window. That's not a regression (switching
 * back to a hidden tab fires `visibilitychange` and `refetchOnWindowFocus` corrects it before the
 * user can act on stale data), just a narrower guarantee than "any tab left open" — not fixed here:
 * `refetchIntervalInBackground: true` would mean every hidden tab, everywhere in the app, polling
 * this endpoint forever, which is a worse trade than the one it would close.
 */
const usePlaygroundMode = () => {
  const query = useQuery({
    queryKey: [queryKeys.playgroundMode],
    queryFn: getPlaygroundModeStatus,
    // staleTime: 0 — this is a safety indicator (suppresses real emails/ERP calls), so every
    // window focus must re-check the true server state rather than trust a cached value. A
    // second tab (or the same tab after a while) must not keep showing "OFF" once the wrong
    // direction to be stale in is "the session is still suppressing and this tab doesn't know
    // it" (review, PR #2577, fp:playgroundmode-staletime-cross-tab). The toggle mutation also
    // updates this query's cache directly on success so the banner reflects state immediately
    // (AC3) rather than waiting on a refetch.
    staleTime: 0,
    refetchOnWindowFocus: true,
    // Auto-review finding (fp:refetch-interval-polls-after-404, PR #2612): a plain 30_000 here
    // ignored the query's own error state, defeating the `retry` rule immediately below it — in
    // every environment where the env gate is off (prod included, where this feature is meant to
    // be dark), every open, logged-in tab would re-poll this 404 twice a minute forever. The
    // function form re-checks the SAME condition `retry` already encodes, so the interval and the
    // retry rule can't drift apart again.
    refetchInterval: (query) =>
      (query.state.error as AxiosError)?.response?.status === 404 ? false : 30_000,
    // A 404 means the env gate is off; retrying it wastes calls and delays "unavailable".
    retry: (failureCount, error) =>
      (error as AxiosError)?.response?.status !== 404 && failureCount < 2
  });

  const isUnavailable = (query.error as AxiosError | null)?.response?.status === 404;
  const isUnknown = query.isError && !isUnavailable && query.data === undefined;

  return {
    isUnavailable,
    isUnknown,
    isActive: query.data?.isActive ?? false,
    isLoading: query.isLoading
  };
};

export default usePlaygroundMode;
